import { Injectable } from '@angular/core';
import { Subject, Observable, BehaviorSubject } from 'rxjs';
import { ComponentPortal } from '@angular/cdk/portal';
import { ModalPortalService } from '@core/services/common/modal-portal.service';
import { ArkikOfflineAlertComponent } from '../../components/arkik-offline-alert/arkik-offline-alert.component';

export interface PlantOfflineAlertConfig {
  message: string;
  type?: 'warning' | 'error';
  plantCode?: string;
  actions?: AlertAction[];
}

export interface AlertAction {
  label?: string; // Text to translate
  description?: string; // Exact text to display
  action: () => void;
  variant?: 'primary' | 'support' | 'danger' | 'text' | 'regular';
  id?: string;
}

/**
 * Service to manage offline alerts for plants
 */
@Injectable({ providedIn: 'root' })
export class PlantOfflineAlertService {
  private _showAlertSubject = new BehaviorSubject<PlantOfflineAlertConfig>(null);
  private _closeAlertSubject = new Subject<void>();
  private _confirmActionSubject = new Subject<void>();

  /**
   * Observable to show a plant offline alert
   */
  public readonly showAlert$: Observable<PlantOfflineAlertConfig> = this._showAlertSubject.asObservable();

  /**
   * Observable to close the alert
   */

  public readonly closeAlert$: Observable<void> = this._closeAlertSubject.asObservable();

  /**
   * Observable to confirm an action in the alert
   */
  public readonly confirmAction$: Observable<void> = this._confirmActionSubject.asObservable();

  constructor(private modalPortalService: ModalPortalService) {}

  /**
   * Shows a plant offline alert
   * @param config Alert configuration
   */
  public showAlert(config: PlantOfflineAlertConfig): void {
    this._showAlertSubject.next(config);

    // Create component dynamically
    this.createDynamicComponent(config);
  }

  /**
   * Closes the currently visible alert
   */
  public closeAlert(): void {
    this._closeAlertSubject.next();
    this.modalPortalService.closeModal();
  }

  /**
   * Confirms the action in the alert
   */
  public confirmAction(): void {
    this._confirmActionSubject.next();
  }

  /**
   * Creates and shows the ArkikOfflineAlert component dynamically
   */
  private createDynamicComponent(config: PlantOfflineAlertConfig): void {
    // Create a portal for the component
    const componentPortal = new ComponentPortal(ArkikOfflineAlertComponent);

    // Add alert to modal service
    this.modalPortalService.components = [componentPortal];
  }
}
